import React, { useState } from 'react';
import { Modal, Box, Button, FormControl, Typography, Grid, TextField, Divider, IconButton, Paper, Fade } from '@mui/material';
import moment from 'moment';
import { Editor, EditorState, RichUtils } from 'draft-js';
import 'draft-js/dist/Draft.css';
import CloseIcon from '@mui/icons-material/Close';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SubjectIcon from '@mui/icons-material/Subject';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';

const LeaveForm = ({ open, onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        date: moment(new Date()).format('YYYY-MM-DD'),
        description: EditorState.createEmpty(),
    });

    const handleDateChange = (e) => {
        setFormData({ ...formData, date: e.target.value });
    };

    const handleDescriptionChange = (editorState) => {
        setFormData({ ...formData, description: editorState });
    };

    const handleKeyCommand = (command, editorState) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);

        if (newState) {
            handleDescriptionChange(newState);
            return 'handled';
        }

        return 'not-handled';
    };

    const applyInlineStyle = (style) => {
        handleDescriptionChange(RichUtils.toggleInlineStyle(formData.description, style));
    };

    const applyBlockType = (blockType) => {
        handleDescriptionChange(RichUtils.toggleBlockType(formData.description, blockType));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData); 
        onClose(); 
    };

    const buttonStyle = {
        textTransform: 'none',
        color: 'black',
        margin: '5px',
    };
        return (
            <Modal 
                open={open} 
                onClose={onClose}
                closeAfterTransition
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Fade in={open}>
                    <Paper
                        elevation={8}
                        sx={{
                            width: '100%',
                            maxWidth: 600,
                            borderRadius: '16px',
                            p: 0,
                            maxHeight: '85vh',
                            overflowY: 'auto',
                            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                        }}
                    >
                        {/* Header */}
                        <Box sx={{ 
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                            p: 2,
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center'
                        }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EventBusyIcon />
                                Leave Request
                            </Typography>
                            <IconButton 
                                onClick={onClose}
                                sx={{ 
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.1)'
                                    }
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Box sx={{ p: 3 }}>
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Leave Date"
                                            type="date"
                                            value={formData.date}
                                            onChange={handleDateChange}
                                            fullWidth
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            InputProps={{
                                                startAdornment: <CalendarTodayIcon sx={{ color: '#f5576c', mr: 1 }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    '&:hover fieldset': {
                                                        borderColor: '#f5576c'
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Subject"
                                            value={formData.subject}
                                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                            fullWidth
                                            placeholder="Brief reason for leave..."
                                            InputProps={{
                                                startAdornment: <SubjectIcon sx={{ color: '#f5576c', mr: 1 }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '12px',
                                                    '&:hover fieldset': {
                                                        borderColor: '#f5576c'
                                                    }
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="body1" sx={{ mb: 2, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <DescriptionIcon sx={{ color: '#f5576c', fontSize: 20 }} />
                                            Detailed Description
                                        </Typography>
                                        <Paper sx={{ 
                                            border: '2px solid #fce7f3', 
                                            borderRadius: '12px', 
                                            p: 2,
                                            backgroundColor: '#fefcfe'
                                        }}>
                                            {/* Rich Text Toolbar */}
                                            <Box sx={{ mb: 2, pb: 1, borderBottom: '1px solid #f3e8ff' }}>
                                                <Grid container spacing={1}>
                                                    {[
                                                        { action: () => applyInlineStyle('BOLD'), label: 'Bold' },
                                                        { action: () => applyInlineStyle('ITALIC'), label: 'Italic' },
                                                        { action: () => applyInlineStyle('UNDERLINE'), label: 'Underline' },
                                                        { action: () => applyBlockType('header-one'), label: 'H1' },
                                                        { action: () => applyBlockType('header-two'), label: 'H2' },
                                                        { action: () => applyBlockType('unordered-list-item'), label: 'List' },
                                                        { action: () => applyBlockType('blockquote'), label: 'Quote' }
                                                    ].map((btn, index) => (
                                                        <Grid item key={index}>
                                                            <Button 
                                                                onClick={btn.action} 
                                                                size="small"
                                                                sx={{
                                                                    minWidth: 'auto',
                                                                    px: 1.5,
                                                                    py: 0.5,
                                                                    fontSize: '12px',
                                                                    color: '#6b7280',
                                                                    border: '1px solid #e5e7eb',
                                                                    borderRadius: '6px',
                                                                    '&:hover': {
                                                                        backgroundColor: '#f9fafb',
                                                                        borderColor: '#f5576c'
                                                                    }
                                                                }}
                                                            >
                                                                {btn.label}
                                                            </Button>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>

                                            <Box sx={{ minHeight: '150px', maxHeight: '250px', overflowY: 'auto' }}>
                                                <Editor
                                                    editorState={formData.description}
                                                    onChange={handleDescriptionChange}
                                                    handleKeyCommand={handleKeyCommand}
                                                    placeholder="Please provide detailed information about your leave request, including reason, duration, and any relevant details..."
                                                />
                                            </Box>
                                        </Paper>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{
                                            p: 2,
                                            backgroundColor: '#fef2f2',
                                            borderRadius: '12px',
                                            border: '1px solid #fecaca',
                                            mb: 3
                                        }}>
                                            <Typography variant="body2" sx={{ color: '#dc2626', fontWeight: 500 }}>
                                                📝 Please ensure all information is accurate. Your leave request will be reviewed by your supervisor.
                                            </Typography>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={onClose}
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    borderColor: '#d1d5db',
                                                    color: '#6b7280',
                                                    borderRadius: '12px',
                                                    px: 3,
                                                    py: 1.5,
                                                    '&:hover': {
                                                        borderColor: '#9ca3af',
                                                        backgroundColor: '#f9fafb'
                                                    }
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                startIcon={<SendIcon />}
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                                    borderRadius: '12px',
                                                    px: 3,
                                                    py: 1.5,
                                                    boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)',
                                                    '&:hover': {
                                                        background: 'linear-gradient(135deg, #ed7de4 0%, #f04561 100%)',
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: '0 6px 20px rgba(245, 87, 108, 0.4)'
                                                    },
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                Submit Request
                                            </Button>
                                        </Box>
                                    </Grid>

                                </Grid>
                            </form>
                        </Box>
                    </Paper>
                </Fade>
            </Modal>
    );
};

export default LeaveForm;
